import axios from "axios";
import axiosRetry from "axios-retry";
import { getToken, waitForToken } from "../utils/authToken";
import { getIdToken } from "../auth/tokenService";

const api = axios.create({
  baseURL: "/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosRetry(api, {
  retries: 3,
  retryDelay: (retryCount) => retryCount * 2000,
  retryCondition: (error) => {
    const requestUrl = error?.config?.url || "";
    if (requestUrl.includes("/generateToken")) {
      return false;
    }

    // 403 removed: retrying an authorization failure never succeeds.
    // 401 is handled by the response interceptor below (refresh + retry once).
    return (
      error.response?.status >= 500 || // Retry on 5xx
      !error.response // Retry on network failure
    );
  },
  shouldResetTimeout: true,
});

if (api) {
  api.interceptors.request.use(
    async (config) => {
      // The CIAM session may not be initialized yet when the first request is
      // fired (e.g. Home's mount effect). Wait for the token instead of
      // sending an unauthenticated request that would fail with 403.
      const token = getToken() || (await waitForToken());
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const token = await getIdToken({ forceRefresh: true });
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
